import React from 'react';
import {
  ArrowRight, Check, Compass, Lightbulb, Target,
  TrendingUp, Users, Zap, Settings, MessageSquare,
  Bot, Workflow, ExternalLink, ChevronRight, LineChart,
  Mail, Globe, Linkedin, Search
} from 'lucide-react';

// Import the standalone components from main site
import HeaderNav from './HeaderNav';
import Contact from './Contact';
import FooterLan from './FooterLan';

// --- Color Palette ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff'
};

const SPACING = {
  xs: 'px-2 py-1',
  sm: 'px-4 py-2',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-16 md:py-20 lg:py-24',
  sectionMedium: 'py-12 md:py-16 lg:py-20',
  sectionCompact: 'py-8 md:py-12 lg:py-16',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-8'
};

// --- Reusable Button Component ---
const Button = ({
  href,
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  icon: IconComponent,
  iconPosition = 'right',
  fullWidth = false,
  className = '',
  type = 'button',
  target,
  rel
}) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2 font-semibold
    transition-all duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    ${fullWidth ? 'w-full' : ''}
    whitespace-nowrap
  `;

  const sizeStyles = {
    xs: 'px-3 py-1.5 text-xs rounded-md',
    sm: 'px-4 py-2 text-sm rounded-md',
    md: 'px-5 py-2.5 text-base rounded-lg',
    lg: 'px-6 py-3 text-lg rounded-lg'
  };

  const variants = {
    primary: `bg-primary hover:bg-primaryDark text-white focus-visible:ring-primary shadow-md hover:shadow-lg active:shadow-sm`,
    secondary: `bg-secondary hover:bg-secondaryDark text-black focus-visible:ring-secondary shadow-md hover:shadow-lg active:shadow-sm`,
    outlinePrimary: `bg-transparent border-2 border-primary text-primary hover:bg-primaryLight focus-visible:ring-primary`,
    outlineGray: `bg-transparent border border-lightGray text-darkGray hover:border-mediumGray hover:text-black focus-visible:ring-mediumGray`,
    text: `bg-transparent text-primary hover:text-primaryDark hover:underline focus-visible:ring-primary`,
    dark: `bg-black hover:bg-black/80 text-white focus-visible:ring-black shadow-md hover:shadow-lg active:shadow-sm`
  };

  const iconElement = IconComponent ? <IconComponent size={size === 'lg' ? 20 : size === 'sm' ? 16 : 18} strokeWidth={2.5} /> : null;

  const content = (
    <>
      {iconPosition === 'left' && iconElement}
      {children}
      {iconPosition === 'right' && iconElement}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
        target={target}
        rel={rel}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
    >
      {content}
    </button>
  );
};

// --- Subpage Navigation (breadcrumbs) Component ---
const SubpageNav = () => {
  return (
    <div className="bg-offWhite py-3 border-b border-lightGray">
      <div className={SPACING.container}>
        <div className="flex items-center text-sm">
          <a href="/" className="text-mediumGray hover:text-primary transition-colors">Home</a>
          <ChevronRight size={14} className="mx-2 text-mediumGray" />
          <a href="/#services" className="text-mediumGray hover:text-primary transition-colors">Services</a>
          <ChevronRight size={14} className="mx-2 text-mediumGray" />
          <span className="text-primary font-medium">Self-Service Innovation</span>
        </div>
      </div>
    </div>
  );
};

// --- Hero Section for Self-Service Innovation ---
const SelfServiceHero = () => {
  return (
    <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 md:pb-20 lg:pb-24 overflow-hidden">
      {/* Dynamic background gradient with branded colors */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primaryLight via-white to-secondaryLight/30 opacity-80"></div>
      
      {/* Abstract branded shapes */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-primaryLight/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      
      <div className={`${SPACING.container} z-10 relative`}>
        <div className="flex flex-col lg:flex-row lg:gap-16 items-center">
          {/* Text Content Area */}
          <div className="lg:w-7/12 text-center lg:text-left">
            <div className="inline-block rounded-full bg-primaryLight px-4 py-1 mb-5">
              <h2 className="text-sm font-semibold text-primary uppercase tracking-wide">
                Self-Service Innovation
              </h2>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-black leading-tight mb-6 md:mb-8">
              <span className="text-primary relative">
                AI-Powered
                <svg className="absolute -bottom-2 left-0 w-full h-2 text-primary" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0,0 Q50,10 100,0" stroke="currentColor" strokeWidth="5" fill="none" />
                </svg>
              </span> Innovation Management
            </h1>

            <p className="text-2xl sm:text-3xl font-bold text-black mb-6">
              Accelerate Innovation with Intelligent Automation
            </p>

            <p className="text-base sm:text-lg text-darkGray mb-8 md:mb-10 lg:mb-12 max-w-2xl mx-auto lg:mx-0">
              Combine advanced AI technology with human-centered design to create customized innovation platforms that streamline processes, enhance collaboration, and drive measurable business outcomes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button href="#contact" variant="primary" size="lg" icon={ArrowRight} className="w-full sm:w-auto">
                Schedule a Consultation
              </Button>
              <Button href="#spark-hub" variant="outlineGray" size="lg" icon={Compass} className="w-full sm:w-auto">
                Explore Spark Innovation Hub
              </Button>
            </div>
          </div>

          {/* Hero Image Area */}
          <div className="lg:w-5/12 mt-12 sm:mt-16 lg:mt-0 w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
            <div className="relative z-20">
              {/* Main image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <img
                  src="/img/ssinno.png"
                  alt="AI-powered innovation platform dashboard"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Floating card element - Analytics */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-3">
                  <Bot size={24} className="text-primary" />
                  <div>
                    <div className="text-xs text-mediumGray font-medium">AI-Powered</div>
                    <div className="text-lg font-bold text-black">Intelligent Automation</div>
                  </div>
                </div>
              </div>
              
              {/* Second floating element - Efficiency */}
              <div className="absolute -top-4 -right-4 bg-secondary/90 text-black rounded-lg shadow-lg py-2 px-4 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="font-bold">End-to-End Innovation Platform</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Overview Section ---
const OverviewSection = () => {
  return (
    <section className="py-16 md:py-20 bg-offWhite">
      <div className={SPACING.container}>
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6">
            <span className="text-primary">Transforming</span> Innovation Management Through Intelligent Automation
          </h2>
          <p className="text-lg text-darkGray">
            Our comprehensive innovation management solution combines AI technology with proven methodologies to streamline processes, enhance collaboration, and accelerate results throughout your organization.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* Feature 1 */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 border border-lightGray/50">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center flex-shrink-0">
                <Settings size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-3">Customized Innovation Infrastructure</h3>
                <p className="text-darkGray">
                  We develop tailored systems that integrate with your existing technology stack and align with your organizational processes, creating an innovation engine specifically designed for your industry challenges and strategic objectives.
                </p>
              </div>
            </div>
          </div>

          {/* Feature 2 */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 border border-lightGray/50">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center flex-shrink-0">
                <Workflow size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-3">Intelligent Workflow Automation</h3>
                <p className="text-darkGray">
                  Our AI-powered workflows transform manual innovation processes into streamlined digital experiences, reducing administrative workload and enabling teams to focus on high-value creative activities that drive business impact.
                </p>
              </div>
            </div>
          </div>

          {/* Feature 3 */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 border border-lightGray/50">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center flex-shrink-0">
                <Users size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-3">Collaborative Innovation Platforms</h3>
                <p className="text-darkGray">
                  Our platforms facilitate cross-functional collaboration by connecting stakeholders throughout your organization, breaking down silos and creating a unified innovation ecosystem that captures and develops ideas from all sources.
                </p>
              </div>
            </div>
          </div>

          {/* Feature 4 */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 border border-lightGray/50">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center flex-shrink-0">
                <TrendingUp size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-3">Performance Analytics & Insights</h3>
                <p className="text-darkGray">
                  Advanced analytics tools provide real-time visibility into your innovation portfolio, measuring key performance indicators and delivering actionable insights that enable data-driven decision making and resource optimization.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Stats Section ---
const StatsSection = () => {
  const stats = [
    {
      value: "63%",
      label: "of organizations implementing Generative AI in innovation processes report significant productivity improvements",
      source: "(Gartner Research, 2024)"
    },
    {
      value: "75%",
      label: "of companies leveraging innovation automation systems achieve sustainable competitive advantages in their markets",
      source: "(McKinsey Global Survey)"
    },
    {
      value: "$4.3M",
      label: "average annual cost savings achieved by enterprises implementing AI-powered innovation management solutions",
      source: "(Forrester Research)"
    },
    {
      value: "87%",
      label: "of executives consider intelligent automation essential for maintaining innovation leadership in rapidly evolving markets",
      source: "(PwC Digital IQ Survey)"
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-r from-primaryLight via-white to-secondaryLight">
      <div className={SPACING.container}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4">
            The Business <span className="text-primary">Impact</span> of Innovation Automation
          </h2>
          <p className="text-darkGray text-lg mb-0">
            Organizations implementing AI-powered innovation platforms achieve measurable advantages in operational efficiency, time-to-market, and strategic agility.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-6 sm:p-8 bg-white rounded-xl shadow-lg border border-lightGray/20">
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-4">{stat.value}</div>
              <p className="text-darkGray mb-2 text-sm sm:text-base">{stat.label}</p>
              <p className="text-sm text-mediumGray">{stat.source}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Spark Hub Section ---
const SparkHubSection = () => {
  const tools = [
    {
      icon: LineChart,
      title: "Trend Discovery & Analysis",
      description: "AI-powered scanning and analysis of market trends, emerging technologies, and industry disruptions."
    },
    {
      icon: Lightbulb,
      title: "Intelligent Ideation",
      description: "Automated idea generation, enhancement, and evaluation using advanced language models and creativity frameworks."
    },
    {
      icon: Target,
      title: "Strategic Alignment Tools",
      description: "Digital tools for aligning innovation efforts with strategic objectives through automated OKR tracking and portfolio management."
    },
    {
      icon: Search,
      title: "Opportunity Identification",
      description: "AI-assisted problem framing and opportunity discovery using advanced 'How Might We' methodologies."
    },
    {
      icon: Users,
      title: "Customer Insight Tools",
      description: "Data-driven user persona creation and empathy mapping for deeper customer understanding and needs analysis."
    },
    {
      icon: TrendingUp,
      title: "Innovation Portfolio Analytics",
      description: "Real-time metrics and visualization of innovation performance across multiple dimensions and projects."
    },
    {
      icon: Compass,
      title: "Roadmap Automation",
      description: "AI-assisted creation and management of innovation roadmaps with automated milestone tracking and dependencies."
    },
    {
      icon: Settings,
      title: "Process Automation Suite",
      description: "End-to-end workflow automation across the entire innovation lifecycle from ideation to implementation."
    }
  ];

  return (
    <section id="spark-hub" className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-white via-primaryLight/10 to-secondaryLight/30 relative overflow-hidden">
      <div className={SPACING.container}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-6">
            Experience <span className="text-primary">Spark Innovation Hub</span>
          </h2>
          <p className="text-lg text-darkGray mx-auto max-w-2xl">
            Our flagship B2C platform showcases the potential of AI-powered innovation management. Explore our suite of intelligent tools designed to transform how organizations approach innovation.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button href="https://spark.lusidea.co" target="_blank" variant="primary" size="lg" icon={ExternalLink}>
              Explore Spark Platform
            </Button>
            <Button href="#contact" variant="outlineGray" size="lg" icon={MessageSquare}>
              Request Custom Demo
            </Button>
          </div>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {tools.map((tool, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-lightGray/50 group">
              <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300">
                {React.createElement(tool.icon, { 
                  size: 24, 
                  className: 'text-primary group-hover:text-white transition-colors duration-300' 
                })}
              </div>
              <h3 className="text-lg font-bold text-black mb-2">{tool.title}</h3>
              <p className="text-sm text-darkGray">{tool.description}</p>
            </div>
          ))}
        </div>
        
        {/* Mock dashboard preview */}
        <div className="mt-16 md:mt-20 relative">
          <div className="w-full h-[400px] rounded-xl overflow-hidden shadow-2xl border-8 border-white">
            <img 
              src="https://placehold.co/1200x800/ce2223/ffffff?text=Spark+Innovation+Hub+Dashboard"
              alt="Spark Innovation Hub Dashboard visualization"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -top-6 -right-6 bg-secondary/90 text-black rounded-lg shadow-lg py-2 px-4 transform rotate-3">
            <div className="font-bold">AI-powered Innovation Platform</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Newsletter Section ---
const NewsletterSection = () => {
  return (
    <section className="py-16 md:py-20 bg-offWhite">
      <div className={SPACING.container}>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-lightGray">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4">
                Subscribe to Our <span className="text-primary">Innovation Tech</span> Digest
              </h2>
              <p className="text-darkGray mb-6">
                Receive monthly insights on AI-powered innovation management and automation trends
              </p>
              <p className="text-darkGray mb-8">
                Our team of innovation and technology experts curates the latest developments in innovation automation, case studies, and implementation strategies.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <input 
                  type="email" 
                  placeholder="Your business email" 
                  className="w-full px-4 py-3 rounded-lg border border-lightGray focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button variant="primary" size="md" icon={ArrowRight} className="whitespace-nowrap">
                  Subscribe
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://placehold.co/400x300/ce2223/ffffff?text=Innovation+Tech+Digest"
                  alt="Innovation technology newsletter preview" 
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-6 -left-6 w-20 h-20 bg-primaryLight rounded-lg -z-10 transform -rotate-6"></div>
              <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-secondaryLight rounded-full -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Main Component for Self-Service Innovation Subpage ---
const SelfServiceInnovationPage = () => {
  return (
    <div className="font-sans antialiased text-darkGray bg-white overflow-x-hidden">
      <HeaderNav />
      <SubpageNav />
      
      <main className="space-y-8 md:space-y-0">
        <SelfServiceHero />
        <OverviewSection />
        <StatsSection />
        <SparkHubSection />
        <NewsletterSection />
        <Contact />
      </main>
      
      <FooterLan />
    </div>
  );
};

export default SelfServiceInnovationPage;